<template>
  <div v-if="selectedCategory === ''">
    <div
      v-for="(group, groupIndex) in groupedProducts"
      :key="group.category"
      ref="groupSection"
      :class="isGroupCollapsible(group) ? 'mb-[260px]' : 'mb-[92px]'"
    >
      <h3 class="text-right text-xl font-bold mb-[16px] pb-[8px]">
        {{ group.category }}
      </h3>
      <div class="mx-auto" :style="categoryContentStyle">
      <!-- The category's own banner, set per webclass in KT. Sits between the
           heading and the products, and renders nothing for the categories
           that have none, which is most of them. -->
      <category-banner
        v-if="group.banner"
        :banner="group.banner"
        :category-name="group.category"
        class="mb-[20px]"
      />
      <div class="relative">
        <div
          ref="grid"
          :class="[
            listView
              ? 'w-full h-fit flex flex-col gap-[20px]'
              : 'w-full h-fit flex-col flex-wrap gap-[21px] grid grid-cols-[repeat(auto-fill,minmax(300px,300px))]',
            isGroupCollapsed(group)
              ? 'products-collapsed-fade pointer-events-none select-none'
              : '',
          ]"
        >
          <product
            v-for="(product, index) in visibleGroupProducts(group)"
            :key="product.id"
            :billingProducts="billingProducts"
            :index="index"
            :listView="listView"
            :product="product"
            :sale="sale"
            @tooltipScaleTrigger="tooltipScaleTrigger"
          />
        </div>
      </div>
      <!-- The collapsed row is masked out to nothing well before its box ends,
           so the gap the reader sees is much smaller than the margin. 88px
           here puts roughly the comp's amount of white between the last thing
           still visible in the row and the button. The 260px below the
           button, down to the next heading, comes from the section wrapper's
           own margin - see isGroupCollapsible(group) there. -->
      <div
        v-if="isGroupCollapsible(group)"
        :class="isGroupCollapsed(group) ? 'mt-[88px]' : 'mt-[20px]'"
        class="flex justify-center"
      >
        <button
          class="flex h-[32px] justify-center items-center gap-[8px] px-[14px] rounded-[5px] bg-primary-700 text-sm text-primary-200 whitespace-nowrap hover:bg-primary-800 hover:shadow-button transition duration-200 min-w-[300px]"
          @click="toggleGroup(group, groupIndex)"
        >
          {{
            (isGroupCollapsed(group) ? "הצג עוד " : "הצג פחות ") +
            group.category
          }}
          <!-- flex-none, or the longer label squeezes the chevron instead of
               letting the button grow past its minimum width -->
          <span class="material-symbols-rounded flex-none text-[20px]">{{
            isGroupCollapsed(group) ? "expand_more" : "expand_less"
          }}</span>
        </button>
      </div>
      </div>
    </div>
  </div>
  <div
    v-else
    :class="
      listView
        ? 'w-full h-fit flex flex-col gap-[20px]'
        : 'w-full h-fit flex-col flex-wrap gap-[21px] grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))]'
    "
  >
    <product
      v-for="(product, index) in productsData"
      :key="product.id"
      :billingProducts="billingProducts"
      :index="index"
      :listView="listView"
      :product="product"
      :sale="sale"
      @tooltipScaleTrigger="tooltipScaleTrigger"
    />
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import Product from "@/components/Order/Product.vue";
import CategoryBanner from "@/components/Global/CategoryBanner.vue";

export default {
  name: "products-list",
  components: { Product, CategoryBanner },
  emits: ["tooltipScaleTrigger", "reachedSecondToLastCategory"],
  data() {
    return {
      expandedCategories: {},
      // A collapsed category (admin flag: collapsed_by_default) keeps exactly
      // one row, clipped to the top of the card - see products-collapsed-fade.
      // The grid is auto-fill, so how many products a row is depends on the
      // width and has to be measured rather than assumed.
      columnsPerRow: 1,
    };
  },
  props: {
    billingProducts: {
      type: Array,
      default: [],
    },
    productsData: {
      type: Array,
      required: true,
    },
    listView: {
      type: Boolean,
      default: false,
    },
    sale: {
      type: Object,
      required: true,
    },
    selectedCategory: {
      type: String,
      default: "",
    },
    searchActive: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    groupedProducts() {
      const groups = [];
      const map = new Map();
      for (const product of this.productsData) {
        const categoryData = product.product?.category || null;
        const category = categoryData?.name || product.category || "אחר";
        if (!map.has(category)) {
          map.set(category, []);
          groups.push({
            category,
            collapsible: Boolean(categoryData?.collapsed_by_default),
            // Serialized by the API's Category model, so it rides along on
            // the product's category relation with nothing extra to fetch.
            banner: categoryData?.banner || null,
            // Stable id for analytics event names (category names are free
            // text and can repeat or change) - null for the "אחר" fallback
            // bucket, which has no real category behind it.
            categoryId: categoryData?.id ?? null,
            products: map.get(category),
          });
        }
        map.get(category).push(product);
      }
      return groups;
    },
    categoryContentStyle() {
      if (this.listView) {
        return { width: "100%" };
      }

      const width =
        this.columnsPerRow * 300 + Math.max(0, this.columnsPerRow - 1) * 21;
      return { width: `min(100%, ${width}px)` };
    },
  },
  methods: {
    firstGroupSection() {
      const sections = this.$refs.groupSection;
      return Array.isArray(sections) ? sections[0] : sections;
    },
    // Measure now, and make sure the observer is attached to whichever grid
    // is currently on screen - it is a different element after a re-render.
    resync() {
      this.updateColumnsPerRow();
      this.watchGridWidth();
      this.watchSecondToLastCategory();
    },
    // The "next sale" popup's other trigger (see Order.vue): a reader
    // scrolling the "הכל" tab is nudged toward the next sale once they reach
    // the second-to-last category, the same way a reader flipping through
    // category tabs one at a time is nudged after their sixth tab. Only
    // meaningful with 2+ groups, and only while "הכל" is the active view -
    // per-category browsing fires the other trigger instead.
    watchSecondToLastCategory() {
      if (typeof IntersectionObserver === "undefined") {
        return;
      }
      if (this.secondToLastObserver) {
        this.secondToLastObserver.disconnect();
        this.secondToLastObserver = null;
      }
      // Three groups at minimum: with two, the "second-to-last" one is the
      // first one - already on screen at scroll 0 - so the observer would
      // report it the instant it starts watching, popup and all, with no
      // scrolling. One or two groups is Order.vue's 60%-scroll trigger's job.
      if (this.selectedCategory !== "" || this.groupedProducts.length < 3) {
        return;
      }
      const sections = this.$refs.groupSection;
      const target = Array.isArray(sections)
        ? sections[sections.length - 2]
        : null;
      if (!target) {
        return;
      }
      this.secondToLastObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.$emit("reachedSecondToLastCategory");
          }
        },
        // Fires once the section's top has scrolled roughly a third of the
        // way up the viewport, rather than only right at the very top edge -
        // reads as "reaching" the section, not "already past" it.
        { threshold: 0, rootMargin: "0px 0px -66% 0px" }
      );
      this.secondToLastObserver.observe(target);
    },
    // The grid can change width without the window doing anything - the cart
    // only joins the layout once the order request comes back, and it can be
    // expanded too. Watching the element itself is what keeps the row count
    // honest through all of that.
    watchGridWidth() {
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const section = this.firstGroupSection();
      if (!section || section === this.observedSection) {
        return;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);
        // Height changes as rows are added or removed, and reacting to that
        // would feed back into itself. Only width decides the column count.
        if (width === this.lastGridWidth) {
          return;
        }
        this.lastGridWidth = width;
        this.updateColumnsPerRow();
      });
      this.resizeObserver.observe(section);
      this.observedSection = section;
    },
    // Derive the fixed card count from the full section, not the constrained
    // content wrapper, so changing the wrapper width cannot lock the layout.
    updateColumnsPerRow() {
      if (this.listView) {
        this.columnsPerRow = 1;
        return;
      }
      const section = this.firstGroupSection();
      if (!section) {
        return;
      }
      const availableWidth = section.clientWidth;
      this.columnsPerRow = Math.max(
        1,
        Math.floor((availableWidth + 21) / (300 + 21))
      );
    },
    // Enough products to need a second row, and not while searching.
    isGroupCollapsible(group) {
      return (
        group.collapsible &&
        !this.searchActive &&
        group.products.length > this.columnsPerRow
      );
    },
    isGroupCollapsed(group) {
      return (
        this.isGroupCollapsible(group) &&
        !this.expandedCategories[group.category]
      );
    },
    visibleGroupProducts(group) {
      return this.isGroupCollapsed(group)
        ? group.products.slice(0, this.columnsPerRow)
        : group.products;
    },
    toggleGroup(group, groupIndex) {
      const wasExpanded = Boolean(this.expandedCategories[group.category]);
      this.expandedCategories[group.category] = !wasExpanded;

      // Only the opening half is reported: the question is how often a reader
      // wants past the fold of a category the admin chose to collapse. Fired
      // twice - once under a shared event name (filterable by category_name
      // once that is registered as a GA4 custom dimension) and once under a
      // name unique to this category, so each one gets its own row in GA4's
      // Events report with no dimension setup required.
      if (!wasExpanded) {
        this.$gtag?.event("category_show_more", {
          category_name: group.category,
          products_total: group.products.length,
        });

        if (group.categoryId != null) {
          this.$gtag?.event(`show_more_category_${group.categoryId}`, {
            category_name: group.category,
            products_total: group.products.length,
          });
        }
      }

      // Collapsing removes everything the reader scrolled past, which would
      // otherwise leave them further down the page than they expect. Put them
      // back on the heading of the category they just closed.
      if (wasExpanded) {
        this.$nextTick(() => this.scrollToGroup(groupIndex));
      }
    },
    scrollToGroup(groupIndex) {
      const sections = this.$refs.groupSection;
      const section = Array.isArray(sections) ? sections[groupIndex] : sections;
      if (!section) {
        return;
      }
      // Clear the sticky nav, measured rather than hard coded so it keeps
      // working with the sale tabs strip above it.
      const nav = document.getElementById("order-nav");
      let offset = 12;
      if (nav) {
        const stickyTop = parseFloat(window.getComputedStyle(nav).top) || 0;
        offset += stickyTop + nav.getBoundingClientRect().height;
      }
      const top = section.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    },
    tooltipScaleTrigger() {
      this.$emit("tooltipScaleTrigger");
    },
  },
  watch: {
    "sale.id"() {
      this.expandedCategories = {};
    },
    listView() {
      this.$nextTick(this.resync);
    },
    productsData() {
      this.$nextTick(this.resync);
    },
    selectedCategory() {
      this.$nextTick(this.resync);
    },
  },
  mounted() {
    this.debouncedUpdateColumns = debounce(this.updateColumnsPerRow, 150);
    window.addEventListener("resize", this.debouncedUpdateColumns);
    this.$nextTick(this.resync);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.debouncedUpdateColumns);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.secondToLastObserver) {
      this.secondToLastObserver.disconnect();
    }
  },
};
</script>

<style>
/* No custom grid column CSS needed; handled by Tailwind's arbitrary value syntax */

/* A collapsed category shows only the top of its one visible row: the product
   image band, with the name, price and controls clipped away beneath it. The
   height matches the card's image area (Product.vue uses h-[175px]), so the
   cut lands on a real boundary in the card instead of part way through the
   text. Masking the row rather than painting a gradient over it means it
   dissolves into whatever is behind it, with no assumption about the page
   background colour. The mask holds full opacity over the first half so the
   visible part stays crisp, and only the tail dissolves. */
.products-collapsed-fade {
  --collapsed-peek: 175px;
  max-height: var(--collapsed-peek);
  overflow: hidden;
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 0%,
    #000 55%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-image: linear-gradient(
    to bottom,
    #000 0%,
    #000 55%,
    rgba(0, 0, 0, 0) 100%
  );
}
</style>